/**
 * Analysis (/dashboard/admin/analysis): the analysis backfill sweep's progress for Chicago Politics staff (read-only;
 * GET /api/dashboard/admin/analysis, worker/src/analysis-sweep.js sweepStatus). Not designed: built from the Access
 * cards and the Approvals tables. Progress (done of the sweep's recordings), spend today against the daily budget,
 * the average cost per meeting, the latest errors and the skipped recordings; when all analysis is paused for billing or
 * credentials (worker/src/analysis-breaker.js), that first, and the recent meetings still without a summary with their
 * next try. Below them, the learned vote-forecast model (worker/src/forecast-status.js): the model in use, the latest
 * nightly training and its holdout scores against the prior and the active model, the frozen forecasts and their
 * prospective accuracy. Paints from snapshot:admin-analysis.
 */
import { useEffect, useState } from "react";
import { lastActiveLabel } from "../account/logic";
import { List, ListRow, RowSub, RowTitle } from "../components/shared/List";
import { Cell, Head } from "./Organizations";
import { analysisPanel, forecastPanel, usd } from "./logic";
import type { AnalysisStatus } from "./types";

const fullDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : undefined);
const count = (n: number) => n.toLocaleString("en-US");

function Bar({ pct, label }: { pct: number; label: string }) {
  return (
    <div className="admin-bar" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

interface Rebuild { startedAt: string; finishedAt: string | null; by: string; users: number | null; error: string | null }

/**
 * Rebuild the Meetings page now (worker/src/dashboard.js admin/meetings-rebuild): every user's following snapshot and
 * the one for people who follow nothing yet, as the hourly job does. For a new office that should see meetings today.
 */
function RebuildMeetings() {
  const [last, setLast] = useState<Rebuild | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const load = () => fetch("/api/dashboard/admin/meetings-rebuild", { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setLast(d.last); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!last || last.finishedAt) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [last?.startedAt, last?.finishedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setBusy(true); setMsg(null);
    const r = await fetch("/api/dashboard/admin/meetings-rebuild", { method: "POST", credentials: "include" }).catch(() => null);
    const d = r ? await r.json().catch(() => null) : null;
    setBusy(false);
    if (d?.last) setLast(d.last);
    if (r?.status === 409) setMsg("A rebuild is already running.");
    else if (r?.status === 403) setMsg("Only staff admins can rebuild.");
    else if (!r?.ok) setMsg("Couldn't start the rebuild. Try again.");
  };
  const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const running = !!last && !last.finishedAt;
  return (
    <section className="access-card" aria-labelledby="meetings-rebuild">
      <h2 id="meetings-rebuild">Meetings page</h2>
      <p className="muted">Rebuilds every user's Meetings page now, including the list people see before they follow anything. It also runs every hour.</p>
      <button className="btn btn--primary btn--small" disabled={busy || running} onClick={start}>{running ? "Rebuilding…" : busy ? "Starting…" : "Rebuild meetings"}</button>
      {last && <p className="muted art-small" role="status">{running ? `Started ${when(last.startedAt)} by ${last.by}.`
        : last.error ? `Last rebuild failed ${when(last.finishedAt!)}: ${last.error}` : `Last rebuilt ${when(last.finishedAt!)} by ${last.by}${last.users != null ? ` (${last.users} people with follows)` : ""}.`}</p>}
      {msg && <p className="art-small art-warn">{msg}</p>}
    </section>
  );
}

export function AdminAnalysis({ initial }: { initial: AnalysisStatus }) {
  useEffect(() => { document.title = "Analysis · Chicago Politics"; }, []);
  const p = analysisPanel(initial);
  const f = forecastPanel(initial.forecast);
  const now = new Date();
  return (
    <div className="access-page admin-page">
      <header className="access-head">
        <h1 className="page-title">Analysis</h1>
        <span className={`chip ${p.paused ? "chip--warning" : p.on ? "chip--positive" : "chip--neutral"}`}>{p.paused ? "Paused" : p.on ? "On" : "Off"}</span>
      </header>
      <RebuildMeetings />

      {p.error ? <p className="empty-panel">{p.error}</p> : <>
        {p.paused && (
          <section className="access-card admin-analysis-paused" role="alert" aria-labelledby="analysis-paused">
            <h2 id="analysis-paused">{p.paused.title}</h2>
            <p className="admin-audit__reason">{p.paused.error}</p>
            <div className="access-rule"><span>Since</span><b title={fullDate(p.paused.since)}>{fullDate(p.paused.since)}</b></div>
            <div className="access-rule"><span>Next check</span><b>{fullDate(p.paused.nextCheck)}</b></div>
            <div className="access-rule"><span>Staff emailed</span><b>{p.paused.emailed ? fullDate(p.paused.emailed) : "No"}</b></div>
          </section>
        )}
        {p.outages.map((o) => (
          <section key={o.key} className="access-card admin-analysis-paused" aria-labelledby={`analysis-out-${o.key}`}>
            <h2 id={`analysis-out-${o.key}`}>{o.title}</h2>
            <p className="admin-audit__reason">{o.error}</p>
            <div className="access-rule"><span>Since</span><b>{fullDate(o.since)}</b></div>
            <div className="access-rule"><span>Next check</span><b>{fullDate(o.nextCheck)}</b></div>
            <div className="access-rule"><span>Staff emailed</span><b>{o.emailed ? fullDate(o.emailed) : "No"}</b></div>
          </section>
        ))}
        <div className="access-cards admin-analysis">
          <section className="access-card" aria-labelledby="analysis-progress">
            <h2 id="analysis-progress">Progress</h2>
            <div className="flags-count"><b>{count(p.progress.done)}</b><span>of {count(p.progress.total)}</span></div>
            <Bar pct={p.progress.pct} label="Progress" />
            <div className="access-rule"><span>Remaining</span><b>{count(p.counts.remaining)}</b></div>
            <div className="access-rule"><span>Running</span><b>{count(p.counts.running)}</b></div>
            <div className="access-rule"><span>Failed</span><b>{count(p.counts.failed)}</b></div>
            <div className="access-rule"><span>Skipped</span><b>{count(p.counts.skipped)}</b></div>
          </section>
          <section className="access-card" aria-labelledby="analysis-spend">
            <h2 id="analysis-spend">Spend today</h2>
            <div className="flags-count"><b>{usd(p.spend.today)}</b><span>of {usd(p.spend.budget)}</span></div>
            <Bar pct={p.spend.pct} label="Spend today" />
            <div className="access-rule"><span>Budget left</span><b>{usd(p.spend.left)}</b></div>
            <div className="access-rule"><span>Total</span><b>{usd(p.spend.total)}</b></div>
          </section>
          <section className="access-card" aria-labelledby="analysis-average">
            <h2 id="analysis-average">Average per meeting</h2>
            <div className="flags-count"><b>{usd(p.average)}</b></div>
          </section>
          <section className="access-card" aria-labelledby="analysis-model">
            <h2 id="analysis-model">Model</h2>
            <div className="access-rule"><span>In use</span><b>{p.model.using}</b></div>
            <div className="access-rule"><span>Setting</span><b>{p.model.setting}</b></div>
            {p.models.map((m) => (
              <div key={m.key} className="access-rule"><span>{m.model}</span><b>{count(m.runs)} · {usd(m.usd)}</b></div>
            ))}
          </section>
          {p.videos && (
            <section className="access-card" aria-labelledby="analysis-videos">
              <h2 id="analysis-videos">Mayor's videos</h2>
              <div className="flags-count"><b>{count(p.videos.ok)}</b><span>of {count(p.videos.total)}</span></div>
              <Bar pct={p.videos.pct} label="Mayor's videos transcribed" />
              {p.videos.rows.filter((r) => r.key !== "ok").map((r) => (
                <div key={r.key} className="access-rule"><span>{r.label}</span><b>{count(r.count)}</b></div>
              ))}
              <div className="access-rule"><span>Last run</span><b title={fullDate(p.videos.lastRun)}>{p.videos.lastRun ? lastActiveLabel(p.videos.lastRun, now) : "—"}</b></div>
              {p.videos.stopped && <div className="access-rule"><span>Stopped</span><b>{p.videos.stopped}</b></div>}
              {p.videos.cookies !== null && <div className="access-rule"><span>Cookies</span><b>{p.videos.cookies ? "Set" : "Not set"}</b></div>}
            </section>
          )}
        </div>

        {f && (
          <section className="approvals-recent" aria-labelledby="analysis-forecast">
            <h2 className="section-title" id="analysis-forecast">Forecast model</h2>
            <div className="access-cards admin-analysis">
              <section className="access-card" aria-labelledby="forecast-active">
                <h2 id="forecast-active">In use</h2>
                <div className="flags-count"><b>{f.active}</b></div>
                <div className="access-rule"><span>Trained</span><b>{fullDate(f.trainedAt) ?? "—"}</b></div>
                <div className="access-rule"><span>Next Council meeting</span><b>{fullDate(f.nextCouncil?.date) ?? "—"}</b></div>
                <div className="access-rule"><span>On its docket</span><b>{f.nextCouncil ? count(f.nextCouncil.docket) : "—"}</b></div>
              </section>
              <section className="access-card" aria-labelledby="forecast-latest">
                <h2 id="forecast-latest">Latest training</h2>
                <div className="flags-count"><b>{f.latest?.version ?? "—"}</b></div>
                {f.latest && <>
                  <div className="access-rule"><span>Trained</span><b>{fullDate(f.latest.trainedAt)}</b></div>
                  <div className="access-rule"><span>Promoted</span><b>{f.latest.promoted ? "Yes" : "No"}</b></div>
                  {f.scores.map((s) => (
                    <div key={s.label} className="access-rule"><span>{s.label}</span><b>{s.candidate} · prior {s.prior} · active {s.active}</b></div>
                  ))}
                  <p className="admin-audit__reason">{f.latest.reason}</p>
                </>}
              </section>
              <section className="access-card" aria-labelledby="forecast-frozen">
                <h2 id="forecast-frozen">Frozen forecasts</h2>
                <div className="flags-count"><b>{count(f.frozen.forecasts)}</b></div>
                <div className="access-rule"><span>Matters</span><b>{count(f.frozen.matters)}</b></div>
                <div className="access-rule"><span>Awaiting a vote</span><b>{count(f.frozen.open)}</b></div>
                <div className="access-rule"><span>Last frozen</span><b>{fullDate(f.frozen.lastFrozenAt) ?? "—"}</b></div>
              </section>
              <section className="access-card" aria-labelledby="forecast-prospective">
                <h2 id="forecast-prospective">Prospective accuracy</h2>
                <div className="flags-count"><b>{f.prospective?.called ?? "—"}</b>{f.prospective && <span>called</span>}</div>
                <div className="access-rule"><span>Roll calls</span><b>{f.prospective ? count(f.prospective.rollCalls) : "—"}</b></div>
                <div className="access-rule"><span>Log loss</span><b>{f.prospective?.logloss ?? "—"}</b></div>
                <div className="access-rule"><span>Brier</span><b>{f.prospective?.brier ?? "—"}</b></div>
              </section>
            </div>
          </section>
        )}

        {p.retrying.length > 0 && (
          <section className="approvals-recent" aria-labelledby="analysis-retrying">
            <h2 className="section-title" id="analysis-retrying">Without a summary <span className="chip chip--neutral chip--medium">{count(p.missingSummary)}</span></h2>
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="analysis-retrying">
              <ListRow as="div" className="members__head ls-head" role="row"><Head min={120}>Meeting</Head><Head width={80}>Attempts</Head><Head width={150}>Next try</Head><Head min={200}>Error</Head></ListRow>
              {p.retrying.map((m) => (
                <ListRow key={m.key} as="div" className="members__row" role="row">
                  <Cell min={120} className="approvals__org"><RowTitle as="b">{m.meeting}</RowTitle><RowSub>{m.date ?? ""}</RowSub></Cell>
                  <Cell width={80} className="members__active">{count(m.attempts)}</Cell>
                  <Cell width={150} className="members__active">{m.next ? fullDate(m.next) : m.state}</Cell>
                  <Cell min={200} className="members__active admin-audit__reason">{m.error ?? ""}</Cell>
                </ListRow>
              ))}
            </List>
          </section>
        )}

        <section className="approvals-recent" aria-labelledby="analysis-errors">
          <h2 className="section-title" id="analysis-errors">Latest errors</h2>
          {p.errors.length === 0 ? <p className="empty-panel">None</p> : (
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="analysis-errors">
              <ListRow as="div" className="members__head ls-head" role="row"><Head min={120}>Meeting</Head><Head min={240}>Error</Head><Head width={110}>When</Head></ListRow>
              {p.errors.map((e) => (
                <ListRow key={e.key} as="div" className="members__row" role="row">
                  <Cell min={120} className="members__active">{e.meeting}</Cell>
                  <Cell min={240} className="members__active admin-audit__reason">{e.error}</Cell>
                  <Cell width={110} className="members__active" title={fullDate(e.when)}>{e.when ? lastActiveLabel(e.when, now) : ""}</Cell>
                </ListRow>
              ))}
            </List>
          )}
        </section>

        {p.skipped.length > 0 && (
          <section className="approvals-recent" aria-labelledby="analysis-skipped">
            <h2 className="section-title" id="analysis-skipped">Skipped</h2>
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="analysis-skipped">
              <ListRow as="div" className="members__head ls-head" role="row"><Head min={120}>Meeting</Head><Head min={240}>Error</Head><Head width={110}>Reason</Head></ListRow>
              {p.skipped.map((m) => (
                <ListRow key={m.key} as="div" className="members__row" role="row">
                  <Cell min={120} className="approvals__org"><RowTitle as="b">{m.meeting}</RowTitle><RowSub>{m.date ?? ""}</RowSub></Cell>
                  <Cell min={240} className="members__active admin-audit__reason">{m.error ?? ""}</Cell>
                  <Cell width={110}><span className="chip chip--warning">{m.reason}</span></Cell>
                </ListRow>
              ))}
            </List>
          </section>
        )}
      </>}
    </div>
  );
}
